import type { ForwardAccuType } from '@/App/tiles/shared/import/common.ts';
import type { AccumulatorScheduleLineResponse } from '@/store/forwardAccumulator/api/tradeCapture/response.model.ts';
import type { TcValidation, Trade, TradeCaptureValidationObject } from '@/store/types.ts';
import type { BidAskPair, Frequency, PredealCheck } from '@/types/common.ts';
import type { Currency, CurrencyPair } from '@/types/currency.ts';
import type { ISODate, Tenor } from '@/types/date.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import type { Timestamp } from '@/utils/date.ts';

type ForwardAccumulatorId = string;

export type ForwardAccumulatorById = Record<ForwardAccumulatorId, FxForwardAccumulator>;

export type FxForwardAccumulatorProductName = FxForwardAccumulator['type'];

export type TcKey<T> = {
  [K in keyof T as `${K & string}String`]: T[K];
};

export type ScheduleError = Partial<{
  [TKey in keyof TcKey<AccumulatorScheduleLineResponse>]: TcValidation;
}>;

export type ScheduleErrors = Record<string, ScheduleError>;
export type ForwardAccumulatorTradeCaptureError = TradeCaptureValidationObject<
  Omit<ForwardAccumulatorProperties, 'schedule'>
> & {
  schedule: ScheduleErrors;
};

export type FxForwardAccumulator = Trade<
  'FxForwardAccumulator',
  ForwardAccumulatorProperties,
  ForwardAccumulatorTradeCaptureError
>;

export type ForwardAccumulatorProperties = {
  currencyPair?: CurrencyPair;

  hedgeType: HedgeType;
  spotHedge?: number;

  accuType: ForwardAccuType;

  isPriceable: boolean;
  tiering?: string;
  isEspDefaultTiering?: boolean;

  clientsWay: ClientsWay;
  execution: {
    status: FxForwardAccumulatorExecutionStatus;
  };
  firstFixingDate?: ISODate;
  firstFixingTenor?: Tenor | null; // Mon / 3M / 90D
  lastFixingDate?: ISODate;
  lastFixingTenor?: Tenor | null; // Mon / 3M / 90D
  premiumDate: ISODate | null;
  premiumDateTenor: Tenor | null;
  premiumPriceType: AccumulatorPriceType;
  premiumTypeCurrencyIndex: CurrencyIndex;
  notional: number | null;
  notionalCurrencyIndex: CurrencyIndex;
  amountMode: AccumulatorAmountMode;
  barrier?: ResponseBarrier | null;

  possibleHedgeTypes: HedgeType[];

  hedgeAmount: number | null;
  hedgeCurrencyIndex: CurrencyIndex;
  hedgePaymentDate: ISODate | null;
  marginCurrency: string;
  numberOfFixings?: number;
  numberOfSettlements?: number;
  leverage?: number;
  leverageAmount?: number;
  strike?: number;

  akoTrigger?: number;
  ekiTrigger?: number;
  step?: number | null;

  scheduleIds?: string[];
  schedule?: Record<string, AccumulatorScheduleLine | null> | null;
  scheduleCustomFields?: ScheduleCustomFields;
  customFields?: CustomFields;
  totalLeverage?: number;
  totalNotional?: number;
  rfs: ForwardAccumulatorRfs;
  rfsVersion: number;
  //accuTypeList?: ForwardAccuType[];
  markupCurrency?: Currency;
  targetProfitType?: string;
  targetProfitTypeList?: string[];
  fixingFrequency?: Frequency;
  fixingFrequenciesList?: Frequency[];
  settlementFrequency?: Frequency;
  settlementFrequencyList?: Frequency[];
  // settlementMode: SettlementMode; // deliveryTypes
  // settlementModeList: SettlementMode[]; // possibleDeliveryTypes;
  isCrossed?: boolean;
  crossCurrency?: string;
  crossCurrenciesList?: string[];
  fixingReference1?: string;
  fixingReference1Default?: string;
  fixingReference1List?: string[];
  fixingReference2?: string;
  fixingReference2Default?: string;
  fixingReference2List?: string[];
  summary: string;
  cashSettlementCurrency?: string;
  // cashSettlementCurrencyList?: string[];
  maxDate?: string;
  mifid: ForwardAccumulatorMifid;

  // Here for MP changes
  stepDown?: number | null;
  stepUp?: number | null;
  calendar?: AccumulatorCalendarLine[];
  expiryDate?: ISODate | null;
  expiryDateTenor?: Tenor | null;
  deliveryDate?: ISODate | null;
  cutOffMarketPlace?: string;
  strikeComparator?: string;

  uiState: {
    displayMode: DisplayMode;
  };
};

export const POSSIBLE_HEDGE_TYPE = ['Live', 'Spot'] as const;
export type HedgeType = (typeof POSSIBLE_HEDGE_TYPE)[number];
export const DEFAULT_HEDGE_TYPE: HedgeType = 'Live';

export const POSSIBLE_CLIENTS_WAY = ['Buy', 'Sell'] as const;
export type ClientsWay = (typeof POSSIBLE_CLIENTS_WAY)[number];
export const DEFAULT_CLIENTS_WAY: ClientsWay = 'Buy';

export const POSSIBLE_PRICE_TYPE = ['Amount'] as const;
export type AccumulatorPriceType = (typeof POSSIBLE_PRICE_TYPE)[number];
export const DEFAULT_PRICE_TYPE: AccumulatorPriceType = 'Amount';

export type PossibleProperty<T = string> = {
  default: T;
  possible: T[];
};

// const POSSIBLE_FORWARD_ACCU_TYPE = [
//   'ForwardAccu', // TODO  was 'TargetAccu' in targetAccumulator, must be checked
//   'PIVOTEKI',
//   'PIVOT',
//   'EKI',
// ] as const;
// export type ForwardAccuType = (typeof POSSIBLE_FORWARD_ACCU_TYPE)[number];
// export const DEFAULT_FORWARD_ACCU_TYPE: ForwardAccuType = 'ForwardAccu';

export type AccumulatorCalendarLine = CommonScheduleCalendarData & {
  stepDown: number | null;
  stepUp: number | null;
  step: number | null;
  amountDown: number | null;
  amountUp: number | null;
  fixingValue: number | null;
  weight: number | null;
  isDelivered: boolean | null;
  amountMid: number | null;
  power: number | null;
  leverage: number | null;
  targetGapDeltaProfit: number | null;
  couponGapDelta: number | null;
};

export type AccumulatorScheduleLine = CommonScheduleCalendarData & {
  //pivot: number | null;
  //ekiTrigger: number | null;
  step: number | null;
  step2: number | null;
  // ekiUp: number | null;
  // ekiDown: number | null;
  // strikeDown: number | null;
  // strikeUp: number | null;
  amount: number | null;
  leverageAmount: number | null;
};

export type CommonScheduleCalendarData = {
  fixingDate: string | null;
  paymentDate: string | null;
  strike: number | null;
};

export type ForwardAccumulatorRfsStatus =
  | 'NOT_PRICEABLE'
  | 'PRICEABLE'
  | 'AWAITING_INDICATIVE'
  | 'AWAITING_EXECUTABLE'
  | 'STREAMING_INDICATIVE'
  | 'STREAMING_EXECUTABLE'
  | 'ERROR'
  | 'BYPASSABLE_ERROR';

export type ForwardAccumulatorRfs =
  | { status: 'AWAITING'; rfsId: string; quoteType: 'Indicative' | 'Tradeable'; traderId?: string }
  | {
      status: 'PRICING';
      rfsId: string;
      quote: ForwardAccumulatorQuote;
      quoteId: string;
      quoteType: 'Indicative' | 'Tradeable';
      traderId?: string;
      initialRfsWindow: number;
    }
  | { status: 'EXPIRED'; quote: ForwardAccumulatorQuote }
  | { status: 'CANCELLED' }
  | { status: 'ERROR'; rfsId?: string; error: StreamingError };

type StreamingError = {
  multipassErrorCode: string;
  errorCode: string;
  errorMessage: string;
};

type LegSide = 'Buy' | 'Sell';
export type Side = LegSide | 'Both';

export const accumulatorAmountModes = ['Fixing', 'Total'] as const;
export type AccumulatorAmountMode = (typeof accumulatorAmountModes)[number];

const settlementModes = ['Cash', 'Physical_Spot', 'Physical_Forward'] as const;
export type SettlementMode = (typeof settlementModes)[number];

export type ResponseBarrier =
  | {
      level: number;
      type: string;
      nature: 'American';
    }
  | {
      level: number;
      type: undefined;
      nature: undefined;
    }
  | {
      level: undefined;
      type: string;
      nature: undefined;
    };

export type ScheduleCustomFields = Record<
  string,
  Array<'FixingDate' | 'Strike' | 'Step' | 'Amount' | 'LeverageAmount' | 'EkiTrigger' | 'PaymentDate'> | undefined
>;

type CustomFields = Array<'SettlementFrequency' | 'FirstFixingDate' | 'Schedule' | 'FixingFrequency'>;

export type ForwardAccumulatorQuote = {
  quoteId: string;
  quoteType: QuoteType;
  rfsWindow: number;
  hedge?: HedgeData;
  predealChecks: PredealCheck[];
  marginData: ForwardAccumulatorRfsMarginDataState;
  quoteContribution: 'Automatic' | 'Manual';

  tiering: string | null;
  premiumData: ForwardAccumulatorRfsPremiumDataState;
  greeks: ForwardAccumulatorRfsGreeksState;
  isEligibleToPremiumRateStoMid: boolean;
  spot: BidAskPair;
  forward: BidAskPair;
  swapPoints: BidAskPair;
  volatility: number;
  frontTimestamp: Timestamp;
  backTimestamp: number;
};

type ForwardAccumulatorRfsGreeksState = {
  deltaCcy1: number;
  deltaCcy2: number;
  vegaCcy1: number;
  vegaCcy2: number;
};

type ForwardAccumulatorRfsPremiumDataState = {
  premiumCcy1: BidAskPair;
  premiumCcy2: BidAskPair;
  premiumPctCcy1: number;
  premiumPctCcy2: number;
  premiumRateStoCcy1Mid: number;
  premiumRateStoCcy2Mid: number;
};

type ForwardAccumulatorRfsMarginDataState = {
  markupCcy1: number;
  markupCcy2: number;
};

interface HedgeData {
  maturityDate: string | undefined;
  rateMid: string | undefined;
  notionalAmount: string | undefined;
  notionalCurrency: string | undefined;
}

type QuoteType = 'Tradeable' | 'Indicative';

interface ForwardAccumulatorMifid {
  rfsDate?: string;
  records: MifidRecord[];
}

interface MifidRecord {
  date: string;
  isError?: boolean;
}

export type FxForwardAccumulatorExecutionStatus = 'PENDING' | 'SUCCESS' | 'REJECTED';
export type DisplayMode = 'Pricer' | 'Scheduler';
